'use client'

import { useEffect, useRef } from 'react'

import { useVerifySmilesUserMutation } from '@/src/api/rtkQuery/smilesApi'
import {
    SMILES_PARAM_ACCOUNT_NUMBER,
    SMILES_PARAM_AUTH_TOKEN,
    SMILES_PARAM_PARTNER_ID,
    SMILES_PARAM_UTM_SOURCE,
    SMILES_UTM_SOURCE
} from '@/src/constants/smiles'
import { selectIsAuthenticated } from '@/src/redux/slices/authSlice'
import { resetSmiles, setSmilesDeepLink, setSmilesVerificationStatus } from '@/src/redux/slices/smilesSlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import type { SmilesDeepLink } from '@/src/types/smiles'
import { isSmilesEnabledForBrand } from '@/src/utils/smiles/isSmilesEnabledForBrand'

const parseDeepLink = (): SmilesDeepLink | null => {
    const params = new URLSearchParams(window.location.search)

    if (params.get(SMILES_PARAM_UTM_SOURCE) !== SMILES_UTM_SOURCE) return null

    const accountNumber = params.get(SMILES_PARAM_ACCOUNT_NUMBER)?.trim() || ''
    const authToken = params.get(SMILES_PARAM_AUTH_TOKEN)?.trim() || ''
    const partnerId = params.get(SMILES_PARAM_PARTNER_ID)?.trim() || ''

    if (!accountNumber || !authToken || !partnerId) return null

    return { accountNumber, authToken, partnerId }
}

const useSmilesEntry = (): void => {
    const dispatch = useAppDispatch()
    const [verifySmilesUser] = useVerifySmilesUserMutation()
    const isSignedIn = useAppSelector(selectIsAuthenticated)
    // Fire verification exactly once.
    const hasVerifiedRef = useRef(false)
    const prevSignedInRef = useRef(isSignedIn)

    // On logout, drop the Smiles session so attribution can't leak onto the next user.
    useEffect(() => {
        if (prevSignedInRef.current && !isSignedIn) {
            dispatch(resetSmiles())
        }
        prevSignedInRef.current = isSignedIn
    }, [dispatch, isSignedIn])

    useEffect(() => {
        if (typeof window === 'undefined' || hasVerifiedRef.current) return
        if (!isSmilesEnabledForBrand()) return

        const deepLink = parseDeepLink()
        if (!deepLink) return

        hasVerifiedRef.current = true
        dispatch(setSmilesDeepLink(deepLink))

        verifySmilesUser({
            accountNumber: deepLink.accountNumber,
            accessToken: deepLink.authToken,
            partnerId: deepLink.partnerId
        })
            .unwrap()
            .then(result => {
                dispatch(setSmilesVerificationStatus(result.status))
            })
            .catch(() => {
                dispatch(setSmilesVerificationStatus('FAILURE'))
            })
    }, [dispatch, verifySmilesUser])
}

export { useSmilesEntry }
export default useSmilesEntry
